// Persists the staff/getAll?isActive=true response in IndexedDB so the UI can
// render staff dropdowns immediately on reload without waiting for the API.
// A separate DB from jms-ref-list-cache keeps the two caches independently
// invalidatable.

const DB_NAME = "jms-staff-cache";
const DB_VERSION = 1;
const STORE_NAME = "keyval";
const STAFF_LIST_KEY = "staffList";

const openDatabase = () =>
  new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error("IndexedDB is not supported in this browser"));
      return;
    }
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const idbGet = async (key) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const request = tx.objectStore(STORE_NAME).get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const idbSet = async (key, value) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

const idbDelete = async (key) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

export const loadCachedStaffList = async () => {
  try {
    const cached = await idbGet(STAFF_LIST_KEY);
    return cached ?? null;
  } catch (error) {
    console.error("Failed to read cached staffList from IndexedDB:", error);
    return null;
  }
};

export const saveCachedStaffList = async (data) => {
  if (!data) return;
  try {
    await idbSet(STAFF_LIST_KEY, data);
  } catch (error) {
    console.error("Failed to persist staffList to IndexedDB:", error);
  }
};

// Call on logout so the next user on this browser does not get served another
// user's cached staff data.
export const clearCachedStaffList = async () => {
  try {
    await idbDelete(STAFF_LIST_KEY);
  } catch (error) {
    console.error("Failed to clear cached staffList from IndexedDB:", error);
  }
};
